import { useState, useCallback } from 'react';
import {
  AddIcon,
  Alert,
  Button,
  CancelIcon,
  Card,
  CheckIcon,
  DeleteIcon,
  Input,
  Spinner,
  Text,
  Tooltip,
} from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import { CondensedPotSummary } from '../CondensedPotSummary';
import { useRejectPot } from '../../hooks/useRejectPot';
import {
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface PotRejectionPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
}

// Reason can be existing (with id) or new (just text)
interface ReasonItem {
  id?: string; // Present for existing reasons
  reason: string;
}

export function PotRejectionPanelContent({
  potDetails,
  onSuccess,
}: PotRejectionPanelContentProps) {
  // Pre-populate with existing unresolved rejection reasons
  const [reasons, setReasons] = useState<ReasonItem[]>(() =>
    potDetails.rejectionReasons
      .filter(r => !r.isResolved)
      .map(r => ({ id: r.id, reason: r.reason }))
  );
  const [currentReason, setCurrentReason] = useState('');

  const { mutate: rejectPot, isPending: isRejecting } = useRejectPot();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isRejecting || isResolving || isRemoving;

  const handleAddReason = useCallback(() => {
    const trimmedReason = currentReason.trim();
    if (trimmedReason && reasons.length < 20) {
      setReasons(prev => [...prev, { reason: trimmedReason }]);
      setCurrentReason('');
    }
  }, [currentReason, reasons.length]);

  const handleRemoveReason = useCallback(
    (index: number) => {
      const reasonToRemove = reasons[index];
      if (reasonToRemove.id) {
        // Existing reason - call API to remove
        removeReason(
          { potId: potDetails.id, reasonId: reasonToRemove.id },
          {
            onSuccess: () => {
              setReasons(prev => prev.filter((_, i) => i !== index));
            },
          }
        );
      } else {
        // New reason - just remove from local state
        setReasons(prev => prev.filter((_, i) => i !== index));
      }
    },
    [reasons, potDetails.id, removeReason]
  );

  const handleResolveReason = useCallback(
    (index: number) => {
      const reasonToResolve = reasons[index];
      if (reasonToResolve.id) {
        resolveReason(
          { potId: potDetails.id, reasonId: reasonToResolve.id },
          {
            onSuccess: () => {
              setReasons(prev => prev.filter((_, i) => i !== index));
            },
          }
        );
      }
    },
    [reasons, potDetails.id, resolveReason]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddReason();
      }
    },
    [handleAddReason]
  );

  const handleConfirmRejection = () => {
    // Get only new reasons (without IDs) to send to the API
    const newReasons = reasons.filter(r => !r.id).map(r => r.reason);

    rejectPot(
      {
        potId: potDetails.id,
        reasons: newReasons,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const canAddReason = currentReason.trim().length > 0 && reasons.length < 20;
  const canReject = reasons.length > 0;
  const newReasonsCount = reasons.filter(r => !r.id).length;

  // Get resolved rejection reasons
  const resolvedRejections = potDetails.rejectionReasons.filter(
    r => r.isResolved
  );

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-6">
          {/* Condensed Pot Summary with rejection form after financials */}
          <CondensedPotSummary
            potDetails={potDetails}
            showDeductions={true}
            renderAfterFinancials={
              <div className="flex flex-col gap-4">
                {/* Add Reason Input */}
                <div>
                  <Text variant="body2" className="mb-2 font-semibold">
                    Rejection Reasons
                  </Text>
                  <div className="flex gap-2">
                    <Input
                      inputSize="sm"
                      placeholder="Enter a rejection reason..."
                      value={currentReason}
                      onChange={e => setCurrentReason(e.target.value)}
                      onKeyDown={handleKeyDown}
                      maxLength={1000}
                      data-testid="rejection-reason-input"
                      disabled={isPending}
                    />
                    <Button
                      variant="outline"
                      onClick={handleAddReason}
                      disabled={!canAddReason || isPending}
                      className="px-4"
                      aria-label="Add rejection reason"
                      data-testid="add-rejection-reason-button"
                    >
                      <AddIcon aria-hidden="true" className="size-5" />
                    </Button>
                  </div>
                  <Text
                    variant="caption"
                    color="secondary"
                    className="mt-1 block"
                  >
                    Press Enter or click + to add. Maximum 20 reasons.
                  </Text>
                </div>

                {/* Reasons List */}
                {reasons.length > 0 && (
                  <div>
                    <Text variant="body2" className="mb-2 font-semibold">
                      Reasons ({reasons.length})
                    </Text>
                    <ul className="m-0 flex list-none flex-col gap-2 rounded-lg bg-surface-raised p-2">
                      {reasons.map((item, index) => (
                        <li
                          key={item.id ?? `new-${index}`}
                          className="flex items-center justify-between gap-2"
                        >
                          <Text variant="body2">{item.reason}</Text>
                          <div className="flex shrink-0 gap-1">
                            {item.id && (
                              <Tooltip content="Mark as resolved">
                                <span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="size-7 text-content-success"
                                    onClick={() => {
                                      handleResolveReason(index);
                                    }}
                                    disabled={isPending}
                                  >
                                    <CheckIcon
                                      aria-hidden="true"
                                      className="size-4"
                                    />
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip content="Remove">
                              <span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-7 text-content-error"
                                  onClick={() => {
                                    handleRemoveReason(index);
                                  }}
                                  disabled={isPending}
                                >
                                  <DeleteIcon
                                    aria-hidden="true"
                                    className="size-4"
                                  />
                                </Button>
                              </span>
                            </Tooltip>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Resolved Reasons Card */}
                {resolvedRejections.length > 0 && (
                  <Card className="border-border-success-strong p-4">
                    <Text variant="h6" color="secondary" className="mb-2">
                      Resolved Reasons ({resolvedRejections.length})
                    </Text>
                    <ul className="m-0 flex list-none flex-col gap-2 rounded-lg bg-surface-raised p-2">
                      {resolvedRejections.map(reason => (
                        <li key={reason.id} className="line-through opacity-70">
                          <Text variant="body2" className="line-through">
                            {reason.reason}
                          </Text>
                          <div className="flex flex-col">
                            <Text as="span" variant="caption" color="secondary">
                              Added by {reason.createdByName}
                            </Text>
                            {reason.resolvedAt && (
                              <Text
                                as="span"
                                variant="caption"
                                className="text-content-success"
                              >
                                Resolved by {reason.resolvedByName} on{' '}
                                {formatDateTime(reason.resolvedAt)}
                              </Text>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* Fixed bottom action area */}
      <div className="border-t border-border-default bg-surface-default p-6">
        {/* Helper text */}
        <Alert severity="warning" className="mb-4">
          Rejecting this pot will notify the owner. They will need to address
          all rejection reasons before resubmitting.
        </Alert>

        <Button
          variant="danger"
          size="lg"
          fullWidth
          onClick={handleConfirmRejection}
          disabled={!canReject || isPending}
          data-testid="confirm-rejection-button"
        >
          {isPending ? (
            <Spinner size={20} color="inherit" />
          ) : (
            <CancelIcon aria-hidden="true" className="size-5" />
          )}
          {isPending
            ? 'Processing...'
            : newReasonsCount > 0
              ? `Reject with ${newReasonsCount} new reason${newReasonsCount !== 1 ? 's' : ''}`
              : `Reject (${reasons.length} existing reason${reasons.length !== 1 ? 's' : ''})`}
        </Button>
      </div>
    </div>
  );
}
